// One person's mood card (W57.0) — the dashboard's mood check-in, split into
// **two cards** instead of two columns of one.
//
// ## Why this is not a column
//
// W47.0 shipped one saturated card with two columns in it (我 left / 對方
// right, a hairline between). At 320px each column was ~85px wide, and the
// line 「你寫了那張紙條 · 14 天前」 was 156px long — so `line-clamp-2` cut
// **46%** of a user-authored sentence. `ADR-0134` recorded that truncation as
// a known and accepted consequence of the shape.
//
// Splitting the card does not merely redraw that shape: it gives each column
// ~140px, which is enough for the line to survive. The clamp stays (a long
// title still has to give way), but the thing it was cutting is no longer
// there to cut.
//
// ## Why the cards are white
//
// They used to be one `surface-aqua` accent block. Two adjacent blocks of the
// same saturated colour read as **one card that got cut in half**, and two of
// them would also have pushed the screen's accent count past
// `MAX_ACCENT_CARDS = 3`. White keeps identity in the **avatar and the emoji**
// — two small colour dots instead of two large blocks — which is the
// 「克制地豐富」 the round asked for.
//
// ## Only the viewer's side is a button
//
// Spec §5.3 is a hard rule (「只能寫自己的, 不可代填對方」) and the backend
// enforces it structurally (`daily_mood/service/checkin.py::upsert_checkin`
// takes the caller's own `user_id` and offers no parameter to write on a
// partner's behalf). Wiring the partner's card to the same handler would
// therefore have had a real, reachable effect: tap 「對方 · 今天還沒記」,
// pick an emoji, and it silently overwrites **my** mood while the partner's
// stays empty. A wrong-effect tap is worse than a dead one, so the partner's
// card renders as plain content with no `press` affordance and nothing for a
// tap to hit (W36.2, ADR-0071).

import { AuthorAvatar } from "@/components/primitives/AuthorAvatar";
import { Card } from "@/components/ui/card";
import type { DashboardActivityItem } from "@/lib/api/dashboard";
// Profile spec §8 B3/B2: one resolver for avatar URLs, and it tolerates the
// null that means "no avatar" without the card having to know that.
import { avatarUrl } from "@/lib/profile/api";
import { DASHBOARD_I18N } from "@/lib/i18n/dashboard";
import { cn } from "@/lib/utils/cn";
import { moodEmojiFor, moodLabelFor } from "./moodCodes";

export type MoodCardSide = "me" | "partner";

export interface MoodCardProps {
  /** Which person this card is about. */
  side: MoodCardSide;
  /**
   * The person's **real** display name.
   *
   * It is the avatar's label, so the initial card shows the right character
   * and the image's alt text is the person's name — not the word 「我」.
   */
  name: string;
  /**
   * What the card's name line **says**.
   *
   * 「我」 for the viewer, the person's display name for the partner. The two
   * are different on purpose (see `MoodPair`'s docstring), which is why this
   * is not simply `name`.
   */
  heading: string;
  /** The avatar URL, or `null` for a member with no picture. */
  avatar: string | null;
  /** The mood code, or `null` when this person has not checked in today. */
  moodCode: string | null;
  /** This person's most recent activity line. */
  activity: DashboardActivityItem | null;
  /**
   * Present **only** on the viewer's side — it opens the mood picker.
   * Its absence is what makes the partner's card inert (see the module
   * docstring), so the prop is the affordance, not a separate boolean.
   */
  onPickMood?: () => void;
}

export function MoodCard({
  side,
  name,
  heading,
  avatar,
  moodCode,
  activity,
  onPickMood,
}: MoodCardProps) {
  const recorded = Boolean(moodCode);
  const tone = side === "me" ? "you" : "partner";
  const interactive = typeof onPickMood === "function";

  const body = (
    <>
      <span className="flex items-center gap-s-2">
        <AuthorAvatar tone={tone} size="sm" label={name} src={avatarUrl(avatar)} />
        <span className="min-w-0 truncate text-meta font-semibold text-ink-1">{heading}</span>
      </span>

      <span className="mt-s-3 flex items-center gap-s-2">
        {recorded ? (
          // Emoji, not a line icon: a mood code is **data** (ADR-0133), the
          // same exemption the diary mood and the WMO weather code have.
          <span aria-hidden className="emoji-font text-title-3 leading-none">
            {moodEmojiFor(moodCode)}
          </span>
        ) : null}
        <span
          className={cn(
            "line-clamp-2 text-balance text-body-sm",
            recorded ? "font-semibold text-ink-1" : "text-ink-3",
          )}
        >
          {recorded ? moodLabelFor(moodCode) : DASHBOARD_I18N.mood.notCheckedIn}
        </span>
      </span>

      {/* The person's own latest line. `line-clamp-2` because a title can be
          arbitrarily long; at ~140px of column width it no longer cuts the
          common case (see the module docstring).

          `mt-auto` bottom-anchors it, so the two cards' activity lines sit at
          the same y even when one of them is one line and the other two
          (W57.0 quick fix — the cards are the same height, so the content has
          to be aligned too, not just the boxes). */}
      <span
        data-testid={`mood-activity-${side}`}
        className="mt-auto block line-clamp-2 pt-s-2 text-balance text-body-sm text-ink-2"
      >
        {activity ? activity.text : DASHBOARD_I18N.activity.empty}
      </span>
    </>
  );

  return (
    <Card
      data-testid={`dashboard-mood-card-${side}`}
      data-side={side}
      className="p-0"
    >
      {interactive ? (
        <button
          type="button"
          onClick={onPickMood}
          data-testid={`mood-pill-${side}`}
          data-recorded={recorded ? "true" : "false"}
          data-interactive="true"
          // The **whole card** is the target: a status card you can tap to
          // change should not make you aim at 20px of text. `min-h-11` is the
          // 44px floor (DESIGN §15) and `w-full` fills the card.
          //
          // `-outline-offset-2` draws the focus ring **inside** the button: the
          // card's own radius means an outside ring would be clipped by it.
          className={cn(
            // `h-full` — the card is a stretched grid item, so the button has
            // to fill it or the tap target stops short of the card's bottom
            // edge on the taller of the two (W57.0 quick fix).
            "press flex h-full min-h-11 w-full flex-col items-stretch rounded-2xl p-s-4 text-left",
            "hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:-outline-offset-2",
          )}
        >
          {body}
        </button>
      ) : (
        // The same markup, without the affordance. A `<span>`-shaped control
        // cannot advertise an action it does not perform.
        <div
          data-testid={`mood-pill-${side}`}
          data-recorded={recorded ? "true" : "false"}
          data-interactive="false"
          className="flex h-full flex-col items-stretch p-s-4"
        >
          {body}
        </div>
      )}
    </Card>
  );
}
